import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Save, RotateCcw, Trash2, Plus, FileText, Lock, Pencil, X } from 'lucide-react';

const EMPTY_RUBRIQUE = {
  numero: '',
  libelle: '',
  regime: '',
  motif_eligibilite: 'EXO_MINERAL_NON_METALLIQUE',
  eligible_exoneration: true,
};

const REGIMES = ['A', 'E', 'D', 'DC', 'A/D', 'E/D'];

const MOTIFS_EXO = [
  { value: 'EXO_MINERAL_NON_METALLIQUE', label: 'Fabrication produits minéraux non métalliques' },
  { value: 'EXO_USAGE_NON_COMBUSTIBLE', label: 'Usage autre que combustible/carburant' },
  { value: 'EXO_DOUBLE_USAGE', label: 'Double usage (matières premières)' },
  { value: 'EXO_PROD_PRODUITS_ENERGETIQUES', label: 'Production de produits énergétiques' },
  { value: 'EXO_PROD_ELECTRICITE', label: "Production d'électricité" },
  { value: 'EXO_BIOGAZ_NON_INJECTE', label: 'Biogaz non injecté' },
];

const AdminIcpeRubriques = () => {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_RUBRIQUE);
  const [showCreate, setShowCreate] = useState(false);

  const canEdit = hasPermission && hasPermission('admin_codes_naf', 'edit');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/icpe-rubriques');
      setItems(data || []);
    } catch (e) {
      toast.error("Erreur chargement des rubriques ICPE");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.numero?.trim() || !form.libelle?.trim()) {
      toast.error('Numéro et libellé obligatoires');
      return;
    }
    try {
      if (editId) {
        const { data } = await api.put(`/admin/icpe-rubriques/${editId}`, form);
        setItems(items.map(it => it.id === editId ? data : it));
        toast.success('Rubrique mise à jour');
      } else {
        const { data } = await api.post('/admin/icpe-rubriques', form);
        setItems([...items, data].sort((a, b) => a.numero.localeCompare(b.numero)));
        toast.success('Rubrique créée');
      }
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm(EMPTY_RUBRIQUE);
    setShowCreate(false);
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({ ...EMPTY_RUBRIQUE, ...item });
    setShowCreate(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer la rubrique ICPE ${item.numero} ?`)) return;
    try {
      await api.delete(`/admin/icpe-rubriques/${item.id}`);
      setItems(items.filter(it => it.id !== item.id));
      toast.success('Rubrique supprimée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser les rubriques ICPE aux valeurs officielles par défaut ? Toutes les modifications seront perdues.')) return;
    try {
      const { data } = await api.post('/admin/icpe-rubriques/reset');
      setItems(data.items || []);
      toast.success(`${data.count} rubriques réinitialisées`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur reset');
    }
  };

  if (!canEdit && hasPermission && !hasPermission('admin_codes_naf', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="admin-icpe-denied">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3">
          <Lock className="w-6 h-6 text-red-600" />
          <div>
            <h2 className="font-semibold text-red-900">Accès refusé</h2>
            <p className="text-sm text-red-800 mt-1">Permission <code>admin_codes_naf.view</code> requise.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="admin-icpe-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Rubriques ICPE — Éligibilité TICGN
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Référentiel des rubriques ICPE permettant de qualifier un site pour l'exonération TICGN
            (notamment au titre des produits minéraux non métalliques).
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-sm px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              data-testid="btn-reset-icpe"
            >
              <RotateCcw className="w-4 h-4" /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={() => { setForm(EMPTY_RUBRIQUE); setEditId(null); setShowCreate(true); }}
              className="inline-flex items-center gap-1 text-sm px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              data-testid="btn-add-icpe"
            >
              <Plus className="w-4 h-4" /> Ajouter une rubrique
            </button>
          </div>
        )}
      </div>

      {showCreate && canEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-3" data-testid="form-icpe">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-blue-900">{editId ? 'Modifier la rubrique' : 'Nouvelle rubrique ICPE'}</h3>
            <button type="button" onClick={cancelEdit} className="text-gray-500 hover:text-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Numéro ICPE *</label>
              <input
                type="text"
                value={form.numero}
                onChange={e => setForm({ ...form, numero: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                placeholder="ex: 2520"
                data-testid="input-numero"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Régime</label>
              <select
                value={form.regime || ''}
                onChange={e => setForm({ ...form, regime: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                data-testid="input-regime"
              >
                <option value="">—</option>
                {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Libellé *</label>
              <input
                type="text"
                value={form.libelle}
                onChange={e => setForm({ ...form, libelle: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                data-testid="input-libelle"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Motif d'éligibilité</label>
              <select
                value={form.motif_eligibilite}
                onChange={e => setForm({ ...form, motif_eligibilite: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded"
                data-testid="input-motif"
              >
                {MOTIFS_EXO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input
                type="checkbox"
                id="eligible_exoneration"
                checked={!!form.eligible_exoneration}
                onChange={e => setForm({ ...form, eligible_exoneration: e.target.checked })}
                data-testid="input-eligible"
              />
              <label htmlFor="eligible_exoneration" className="text-sm">Éligible à l'exonération TICGN</label>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center gap-1 text-sm px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              data-testid="btn-save-icpe"
            >
              <Save className="w-4 h-4" /> Enregistrer
            </button>
            <button type="button" onClick={cancelEdit} className="px-4 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
              Annuler
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500" data-testid="loading">Chargement…</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left">N°</th>
                <th className="px-4 py-2 text-left">Libellé</th>
                <th className="px-4 py-2 text-center w-20">Régime</th>
                <th className="px-4 py-2 text-left">Motif</th>
                <th className="px-4 py-2 text-center w-24">Éligible</th>
                {canEdit && <th className="px-4 py-2 w-24"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-testid="tbody-icpe">
              {items.map(it => (
                <tr key={it.id} data-testid={`row-${it.numero}`}>
                  <td className="px-4 py-2 font-mono font-semibold">{it.numero}</td>
                  <td className="px-4 py-2 text-gray-700">{it.libelle}</td>
                  <td className="px-4 py-2 text-center text-gray-600">{it.regime || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {(MOTIFS_EXO.find(m => m.value === it.motif_eligibilite) || {}).label || it.motif_eligibilite}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {it.eligible_exoneration ? (
                      <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">Oui</span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">Non</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => handleEdit(it)}
                          className="text-blue-500 hover:text-blue-700"
                          data-testid={`btn-edit-${it.numero}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(it)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`btn-delete-${it.numero}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={canEdit ? 6 : 5} className="px-4 py-6 text-center text-gray-400 text-sm">Aucune rubrique. Cliquez sur "Réinitialiser" pour charger les rubriques officielles.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminIcpeRubriques;
